import * as React from 'react';
import type { RecordRef, RecordSeed, RecordType } from '../../lib/record-detail.js';

export interface RecordNavigator {
  type: RecordType;
  ids: readonly string[];
  seedFor: (id: string) => RecordSeed | null;
}

interface NavigatorContextValue {
  navigator: RecordNavigator | null;
  setNavigator: React.Dispatch<React.SetStateAction<RecordNavigator | null>>;
}

const NavigatorContext = React.createContext<NavigatorContextValue | null>(null);

export function RecordNavigatorProvider({ children }: { children: React.ReactNode }) {
  const [navigator, setNavigator] = React.useState<RecordNavigator | null>(null);
  const value = React.useMemo(() => ({ navigator, setNavigator }), [navigator]);
  return <NavigatorContext.Provider value={value}>{children}</NavigatorContext.Provider>;
}

/** The visible list registers its row order so the detail host can step prev/next. */
export function useRegisterRecordNavigator(navigator: RecordNavigator): void {
  const setNavigator = React.useContext(NavigatorContext)?.setNavigator;
  const seedForRef = React.useRef(navigator.seedFor);
  seedForRef.current = navigator.seedFor;
  const { type, ids } = navigator;
  React.useEffect(() => {
    if (!setNavigator) return;
    const registered: RecordNavigator = { type, ids, seedFor: (id) => seedForRef.current(id) };
    setNavigator(registered);
    return () => setNavigator((current) => (current === registered ? null : current));
  }, [ids, setNavigator, type]);
}

export function useRecordNavigator(): RecordNavigator | null {
  return React.useContext(NavigatorContext)?.navigator ?? null;
}

export function neighborIds(
  navigator: RecordNavigator | null,
  ref: RecordRef | null,
): { prevId: string | null; nextId: string | null } {
  if (!navigator || !ref || navigator.type !== ref.type) return { prevId: null, nextId: null };
  const index = navigator.ids.indexOf(ref.id);
  if (index < 0) return { prevId: null, nextId: null };
  return {
    prevId: index > 0 ? (navigator.ids[index - 1] ?? null) : null,
    nextId: index < navigator.ids.length - 1 ? (navigator.ids[index + 1] ?? null) : null,
  };
}
